import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useLang } from "@/context/LanguageContext";
import { ZoomIn } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

const ASPECT = 3 / 1; // banner ratio

async function getCroppedBase64(src, cropPixels, maxWidth = 1600) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
  const scale = cropPixels.width > maxWidth ? maxWidth / cropPixels.width : 1;
  const outW = Math.round(cropPixels.width * scale);
  const outH = Math.round(cropPixels.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height, 0, 0, outW, outH);
  return canvas.toDataURL("image/jpeg", 0.85);
}

export default function CoverCropper({ src, open, onClose, onCropped }) {
  const { t } = useLang();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_area, pixels) => setAreaPixels(pixels), []);

  async function save() {
    if (!areaPixels) return;
    setBusy(true);
    try {
      const b64 = await getCroppedBase64(src, areaPixels);
      await onCropped(b64);
      onClose();
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="cover-cropper">
        <DialogHeader>
          <DialogTitle className="font-display">{t("crop.title")}</DialogTitle>
          <DialogDescription>{t("crop.desc")}</DialogDescription>
        </DialogHeader>
        <div className="relative w-full h-64 bg-slate-900 rounded-xl overflow-hidden">
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={ASPECT}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              objectFit="contain"
            />
          )}
        </div>
        <div className="flex items-center gap-3 px-1">
          <ZoomIn size={18} className="text-slate-400 shrink-0" />
          <Slider value={[zoom]} min={1} max={3} step={0.05} onValueChange={(v) => setZoom(v[0])} data-testid="cover-zoom" className="flex-1" />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="cover-crop-cancel">{t("c.cancel")}</Button>
          <Button onClick={save} disabled={busy} className="rounded-xl font-semibold" data-testid="cover-crop-save">
            {busy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("crop.saving")}</span> : t("c.save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
